import { bugtracker } from '@99designs/design-services-common';
import { BriefConfig, BriefTenant, DependentFact, TenantType } from '@99designs/graph-utils/types';
import { __, __url, getSdpUrl } from '@99designs/i18n';
import { LogoNationalPen, LogoVistaprint, TenantLogo } from '@99designs/swan-wizard';
import {
    BriefProductFragment,
    BriefRequestFragment,
} from '../../components/BriefForm/brief.generated';

export type Tenant = {
    logo: TenantLogo;
    logoAria: string;
    helpRoute: string;
    helpLabel: string;
    showProgressBar: boolean;
    reviewTitle: string;
    reviewSubtitle: string;
    exit: (mpvID?: string) => string;
    title: (title: string, req: NullableRequest, product: NullableProduct) => string;
};

type NullableRequest = BriefRequestFragment | null;
type NullableProduct = BriefProductFragment | null;

// Configuration extends the Graph BriefConfig type to support additional, non-graph-provided options.
export type Configuration = BriefConfig & { tenant: BriefTenant & Tenant };

export const configure = (incoming: BriefConfig): Configuration => {
    return {
        ...incoming,
        tenant: {
            ...incoming.tenant,
            ...getTenant(incoming?.tenant?.id ?? 'VISTAPRINT'),
        },
    };
};

const getTenant = (id: TenantType): Tenant => {
    const tenants: Record<TenantType, Tenant> = {
        VISTAPRINT: {
            logo: LogoVistaprint,
            logoAria: __('Vistaprint Home Logo'),
            helpLabel: __('Help center'),
            helpRoute: '/customer-care/help-center/',
            showProgressBar: true,
            reviewTitle: __('Give your brief a once-over'),
            reviewSubtitle: __('Take a moment to review your design project details'),
            exit: (mpvID?: string): string => {
                return mpvID ? getSdpUrl(mpvID) : __url('/design');
            },
            title: (title: string, request: NullableRequest, product: NullableProduct): string => {
                return (
                    title || request?.expertService?.title || product?.title || __('Custom Design')
                );
            },
        },
        NATIONAL_PEN: {
            logo: LogoNationalPen,
            logoAria: 'National Pen Home Logo',
            helpLabel: '',
            helpRoute: '/',
            showProgressBar: false,
            reviewTitle: 'Are you ready to submit your designs?',
            reviewSubtitle: 'Take a moment to review your design details',
            exit: (mpvID?: string): string => {
                return '/';
            },
            title: (title: string, request: NullableRequest, product: NullableProduct): string => {
                return '';
            },
        },
    };

    return tenants[id];
};

export const getDefault = (): Configuration => {
    return {
        __typename: 'BriefConfig',
        tenant: {
            __typename: 'BriefTenant',
            id: 'VISTAPRINT',
            name: 'Vistaprint',
            ...getTenant('VISTAPRINT'),
        },
        showDta: true,
        showReview: true,
        showHowItWorks: true,
        showDesignLiveSelector: true,
        showProductCard: true,
        showSubtitle: true,
        howItWorks: '',
        subtitleCopy: '',
        showTitle: true,
        showHelp: true,
        showExit: true,
        submitWithCart: true,
        submitButtonCopy: '',
        submitButtonEditCopy: '',
        experience: 'MULTI_STEP',
        titleCopy: '',
        exitTo: 'DEFAULT',
        singleFileUploads: false,
        requiresFacts: [],
    };
};

export const satisfiesRequiredFact = (
    target: DependentFact,
    required: DependentFact[],
    request: BriefRequestFragment | null
): boolean => {
    const factValidators: Record<DependentFact, (request: BriefRequestFragment | null) => boolean> =
        {
            PRINT_SIZE: (request: BriefRequestFragment | null) => {
                const fact = request?.optionalFacts?.printOutcome?.options?.encoded;

                if (!fact) return false;

                try {
                    const decoded = JSON.parse(fact);
                    return Object.keys(decoded).some((key) => key.toLowerCase() === 'size');
                } catch (e) {
                    bugtracker.notify(
                        new Error(
                            'Failed to parse required fact for brief: printOutcome.options.encoded'
                        )
                    );
                    return false;
                }
            },
        };

    if (required.includes(target)) {
        return factValidators[target](request) ?? false;
    }

    return true;
};
